/**
 * Authentication types for Cognito integration.
 */

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number; // Unix timestamp in ms
}

export interface AuthUser {
  id: string;
  email: string;
  tenantId: string;
  role?: string | undefined;
  departmentId?: string | undefined;
  name?: string | undefined;
  emailVerified: boolean;
}

export interface AuthResult {
  user: AuthUser;
  tokens: AuthTokens;
}

export interface ChallengeResult {
  challengeName: 'NEW_PASSWORD_REQUIRED' | 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';
  session: string;
  challengeParameters?: Record<string, string> | undefined;
}

export type AuthResponse =
  | { type: 'success'; data: AuthResult }
  | { type: 'challenge'; data: ChallengeResult };

/**
 * Cognito error codes mapped to user-friendly messages.
 */
export const COGNITO_ERROR_MESSAGES: Record<string, string> = {
  NotAuthorizedException: 'Invalid email or password',
  UserNotFoundException: 'No account found with this email',
  UserNotConfirmedException: 'Please verify your email before signing in',
  PasswordResetRequiredException: 'You must reset your password before signing in',
  InvalidPasswordException: 'Password does not meet requirements',
  UsernameExistsException: 'An account with this email already exists',
  CodeMismatchException: 'Invalid verification code',
  ExpiredCodeException: 'Verification code has expired',
  LimitExceededException: 'Too many attempts. Please try again later',
  InvalidParameterException: 'Invalid input provided',
  TooManyRequestsException: 'Too many requests. Please wait and try again',
};

export type CognitoErrorCode = keyof typeof COGNITO_ERROR_MESSAGES;
